import { create } from 'zustand'
import type { Notification } from '@/types'
import * as api from '@/services/api'

/**
 * 移动端通知 Store — 负责拉取、SSE 订阅、全局未读 badge。
 * 在 App.tsx 的 checkAuth 成功后会触发 connectStream()。
 */
interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean
  isConnected: boolean

  fetch: () => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  remove: (id: string) => Promise<void>
  clear: () => Promise<void>

  connectStream: () => void
  disconnectStream: () => void
}

const TOKEN_KEY = 'qd_token'

let sseSource: EventSource | null = null
let sseReconnectTimer: ReturnType<typeof setTimeout> | null = null

function rawToNotification(raw: Record<string, unknown>): Notification {
  return {
    id: raw.id as string,
    type: (raw.type as Notification['type']) || 'system',
    priority: (raw.priority as Notification['priority']) || 'medium',
    category: (raw.category as string) || undefined,
    title: (raw.title as string) || '',
    message: (raw.content as string) || '',
    is_read: !!raw.isRead,
    created_at: (raw.timestamp as string) || '',
    action_url: (raw.actionUrl as string) || null,
    action_label: (raw.actionLabel as string) || null,
    metadata: (raw.metadata as Record<string, unknown>) || undefined,
    group_key: (raw.groupKey as string) || undefined,
  }
}

function computeUnread(list: Notification[]): number {
  return list.reduce((n, x) => n + (x.is_read ? 0 : 1), 0)
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  isConnected: false,

  fetch: async () => {
    try {
      set({ isLoading: true })
      const data = await api.getNotifications(100)
      set({
        notifications: data.notifications,
        unreadCount: typeof data.unreadCount === 'number' ? data.unreadCount : computeUnread(data.notifications),
        isLoading: false,
      })
    } catch {
      set({ isLoading: false })
    }
  },

  fetchUnreadCount: async () => {
    try {
      const n = await api.getUnreadCount()
      set({ unreadCount: n })
    } catch {
      // ignore
    }
  },

  markRead: async (id) => {
    const target = get().notifications.find((n) => n.id === id)
    if (!target || target.is_read) return
    set((state) => {
      const notifications = state.notifications.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      return { notifications, unreadCount: computeUnread(notifications) }
    })
    try {
      await api.markNotificationsRead([id])
    } catch {
      // 下次 fetch 会矫正
    }
  },

  markAllRead: async () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, is_read: true })),
      unreadCount: 0,
    }))
    try {
      await api.markNotificationsRead()
    } catch {
      // ignore
    }
  },

  remove: async (id) => {
    const snapshot = get().notifications
    set((state) => {
      const notifications = state.notifications.filter((n) => n.id !== id)
      return { notifications, unreadCount: computeUnread(notifications) }
    })
    try {
      await api.deleteNotification(id)
    } catch {
      set({ notifications: snapshot, unreadCount: computeUnread(snapshot) })
    }
  },

  clear: async () => {
    const snapshot = get().notifications
    set({ notifications: [], unreadCount: 0 })
    try {
      await api.clearNotifications()
    } catch {
      set({ notifications: snapshot, unreadCount: computeUnread(snapshot) })
    }
  },

  connectStream: () => {
    if (sseSource) return
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return

    const url = `/api/notifications/stream?token=${encodeURIComponent(token)}`
    try {
      const es = new EventSource(url)
      sseSource = es

      es.addEventListener('hello', () => set({ isConnected: true }))
      es.addEventListener('heartbeat', () => set({ isConnected: true }))

      es.addEventListener('notification', (evt) => {
        try {
          const ev = JSON.parse((evt as MessageEvent).data) as { notification?: Record<string, unknown> }
          if (ev?.notification) {
            const n = rawToNotification(ev.notification)
            set((state) => {
              const exists = state.notifications.some((x) => x.id === n.id)
              const notifications = exists
                ? state.notifications.map((x) => (x.id === n.id ? n : x))
                : [n, ...state.notifications]
              return { notifications, unreadCount: computeUnread(notifications) }
            })
          }
        } catch {
          // ignore
        }
      })

      es.addEventListener('read', () => {
        void get().fetch()
      })

      es.addEventListener('delete', (evt) => {
        try {
          const ev = JSON.parse((evt as MessageEvent).data) as { id?: string }
          if (ev?.id) {
            set((state) => {
              const notifications = state.notifications.filter((n) => n.id !== ev.id)
              return { notifications, unreadCount: computeUnread(notifications) }
            })
          }
        } catch {
          // ignore
        }
      })

      es.addEventListener('clear', () => {
        set({ notifications: [], unreadCount: 0 })
      })

      es.onerror = () => {
        set({ isConnected: false })
        try {
          es.close()
        } catch {
          // ignore
        }
        sseSource = null
        if (sseReconnectTimer) clearTimeout(sseReconnectTimer)
        sseReconnectTimer = setTimeout(() => {
          const t2 = localStorage.getItem(TOKEN_KEY)
          if (t2) get().connectStream()
        }, 5000)
      }
    } catch {
      set({ isConnected: false })
    }
  },

  disconnectStream: () => {
    if (sseReconnectTimer) {
      clearTimeout(sseReconnectTimer)
      sseReconnectTimer = null
    }
    if (sseSource) {
      try {
        sseSource.close()
      } catch {
        // ignore
      }
      sseSource = null
    }
    set({ isConnected: false })
  },
}))
